import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { LogOut, ShieldCheck } from "lucide-react";
import { adminFetch } from "../../lib/adminApi";

// Three-state auth gate: null = checking, false = redirect to login, user object = render.
export const AdminShell = ({ title, children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checked, setChecked] = useState(!!location.state?.user);

  useEffect(() => {
    if (checked) return;
    adminFetch("/api/auth/me").then((u) => { setUser(u); setChecked(true); })
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [checked, navigate]);

  const logout = async () => {
    try { await adminFetch("/api/auth/logout", { method: "POST" }); } catch { /* ignore */ }
    navigate("/admin/login", { replace: true });
  };

  if (!checked || !user) return <div className="min-h-screen bg-[#0A0A0A] pt-32 text-center text-gray-500" data-testid="admin-checking">Verifying access…</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 pb-16" data-testid="admin-shell">
      <Helmet><title>{`${title} · Highwall Admin`}</title><meta name="robots" content="noindex,nofollow" /></Helmet>
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 py-4 border-b border-white/10 mb-6">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-signal-red" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-gray-400">Highwall Internal</span>
            <h1 className="font-heading text-2xl font-bold uppercase text-white" data-testid="admin-title">{title}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <nav className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest" data-testid="admin-nav">
              <Link to="/admin/orders" className={`hover:text-white ${location.pathname === "/admin/orders" ? "text-signal-red" : "text-gray-400"}`} data-testid="admin-nav-orders">Orders</Link>
              <Link to="/admin/readiness" className={`hover:text-white ${location.pathname === "/admin/readiness" ? "text-signal-red" : "text-gray-400"}`} data-testid="admin-nav-readiness">Readiness</Link>
            </nav>
            <span className="text-gray-400" data-testid="admin-user-email">{user.email}</span>
            <button type="button" onClick={logout} className="inline-flex items-center gap-2 px-3 py-2 border border-white/20 text-gray-200 hover:border-signal-red hover:text-white transition-colors" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};
